/**
 * The Culture area is a single route whose view is chosen by query params:
 *
 *   ?type=report&tab=<slug>&campaign=<sk>   the tabbed culture page
 *   ?type=explorer                          the culture data explorer
 *   ?type=hotspots                          the alignment hotspots view
 *   ?type=generate&campaign=<sk>            the culture report generation flow
 *   ?type=insights&campaign=<sk>            the generated culture insights report
 *   ?type=insights&campaign=<sk>&entity=<id>  one division or group's summary of it
 *   (no type)                               the culture insights landing page
 */

export const CULTURE_TAB_SLUGS = [
  "dimensions",
  "alignment",
  "subcultures",
  "engagement",
  "feedback",
] as const;

export type CultureTabSlug = (typeof CULTURE_TAB_SLUGS)[number];

export type CultureViewType =
  | "report"
  | "explorer"
  | "hotspots"
  | "generate"
  | "insights";

const CAMPAIGN_PARAM = "campaign";
const ENTITY_PARAM = "entity";
const TAB_PARAM = "tab";
const TYPE_PARAM = "type";

/**
 * Which of the area's views the URL is asking for, or `undefined` for the landing page.
 *
 * A helper rather than the param read where it is needed, because the app shell reads it too: the
 * insights report hides the top nav, and it should not be a second place that knows the param's
 * name and the spelling of every value.
 */
export const readViewType = (search: string) =>
  (new URLSearchParams(search).get(TYPE_PARAM) as CultureViewType | null) ??
  undefined;

/** The sort key the legacy engage queries take, e.g. `CAM$<uuid>`. */
export const readCampaignSk = (search: string) =>
  new URLSearchParams(search).get(CAMPAIGN_PARAM);

const CAMPAIGN_SK_PREFIX = "CAM$";

/**
 * The bare uuid, which is what the engage subgraph keys campaigns on — the older sibling
 * queries take the prefixed sort key, and the graphql guidelines tell us not to copy them.
 */
export const campaignIdFromSk = (sk: string | null | undefined) =>
  sk?.startsWith(CAMPAIGN_SK_PREFIX) ? sk.slice(CAMPAIGN_SK_PREFIX.length) : sk;

/** The same id, for the views that read their campaign off the URL rather than a list. */
export const readCampaignId = (search: string) =>
  campaignIdFromSk(readCampaignSk(search));

/**
 * The division or group whose summary the report is scoped to, or `null` for the whole
 * organisation.
 *
 * In the URL rather than in the page's state so a team's summary can be linked to and so the back
 * button leaves the report the way the reader found it, which is how the area already treats the
 * campaign and the tab.
 */
export const readEntityId = (search: string) =>
  new URLSearchParams(search).get(ENTITY_PARAM);

export const readTabIndex = (search: string) =>
  tabIndexForSlug(new URLSearchParams(search).get(TAB_PARAM));

export const tabIndexForSlug = (slug: string | null) => {
  const index = CULTURE_TAB_SLUGS.indexOf(slug as CultureTabSlug);
  return index === -1 ? 0 : index;
};

export const slugForTabIndex = (index: number): CultureTabSlug =>
  CULTURE_TAB_SLUGS[index] ?? CULTURE_TAB_SLUGS[0];

/** Builds a fresh search string, for links that navigate away from the current view. */
export const cultureSearch = ({
  type,
  tab,
  campaignSk,
}: {
  type?: CultureViewType;
  tab?: CultureTabSlug;
  campaignSk?: string;
}) => {
  const params = new URLSearchParams();

  if (type) params.set(TYPE_PARAM, type);
  if (tab) params.set(TAB_PARAM, tab);
  if (campaignSk) params.set(CAMPAIGN_PARAM, campaignSk);

  return `?${params.toString()}`;
};

/** Replaces a single param, for updates that should keep the user where they are. */
export const searchWithCampaign = (search: string, campaignSk: string) =>
  searchWithParam(search, CAMPAIGN_PARAM, campaignSk);

export const searchWithTab = (search: string, tab: CultureTabSlug) =>
  searchWithParam(search, TAB_PARAM, tab);

/** The whole organisation has no id of its own, so choosing it drops the param rather than sets it. */
export const searchWithEntity = (search: string, entityId?: string) => {
  if (!entityId) return searchWithoutParam(search, ENTITY_PARAM);

  return searchWithParam(search, ENTITY_PARAM, entityId);
};

const searchWithParam = (search: string, name: string, value: string) => {
  const params = new URLSearchParams(search);
  params.set(name, value);

  return `?${params.toString()}`;
};

const searchWithoutParam = (search: string, name: string) => {
  const params = new URLSearchParams(search);
  params.delete(name);

  return `?${params.toString()}`;
};
